import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getCustomerByIdd } from "../services/customerService";
import {
  CustomercheckAuthStatus,
  logoutCustomer,
} from "../services/authService";

export const AuthContext = createContext({
  user: null,
  loading: true,
  fetchUser: () => {},
  handleAuthSuccess: () => {},
  logout: () => {},
  refreshUser: () => {},
});

const normalizeBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true";
  }
  return false;
};

const normalizeString = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const mapUserData = useCallback((authData, rawProfile) => {
    const profile = Array.isArray(rawProfile) ? rawProfile[0] : rawProfile;

    return {
      ...authData,
      ...profile,
      userId: normalizeString(profile?.AC_USER_ID || authData?.userId),
      fullName: normalizeString(profile?.FULL_NAME || authData?.fullName),
      email: normalizeString(profile?.EMAIL || authData?.email),
      companyName: normalizeString(
        profile?.COMPANY_NAME || authData?.companyName,
      ),
      companyAbbreviation: normalizeString(
        profile?.COMPANY_ABBRIVIATION || authData?.companyAbbreviation,
      ),
      countryId: normalizeString(profile?.LT_COUNTRY_ID),
      countryName: normalizeString(profile?.COUNTRY_NAME),
      partitionName: normalizeString(profile?.PARTITION_NAME),
      emailVerified: normalizeBoolean(profile?.EMAIL_VERIFIED),
      trialEligible: normalizeBoolean(profile?.TRIAL_ELIGIBLE),
      trialStartDate: normalizeString(profile?.TRIAL_START_DATE),
      trialEndDate: normalizeString(profile?.TRIAL_END_DATE),

      //  لایسنس :
      licenseIsActive: normalizeBoolean(profile?.LICENSE_IS_ACTIVE),
      hasExpired: normalizeBoolean(profile?.HAS_EXPIRED),
      expirationDate: normalizeString(profile?.EXPIRATION_DATE),
      activationDate: normalizeString(profile?.ACTIVATION_DATE),
      remainingDays: profile?.REMAINING_DAYS
        ? Number(profile.REMAINING_DAYS)
        : null,
      isFreeLicense: normalizeBoolean(profile?.IS_FREE_LICENSE),
      licenseNumber: normalizeString(profile?.LICENSE_NUMBER),
    };
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const authData = await CustomercheckAuthStatus();

      if (
        authData &&
        authData.authenticated !== false &&
        authData.access !== "false"
      ) {
        const profileData = await getCustomerByIdd();
        const mappedUser = mapUserData(authData, profileData);
        setUser(mappedUser);
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error("Auth check failed:", err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [mapUserData]);

  // فقط اولین باری که اپلیکیشن لود می‌شود وضعیت لاگین را چک می‌کنیم
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleAuthSuccess = useCallback(
    async (result, redirectPath, options = {}) => {
      const shouldNavigate = options.navigate !== false;

      if (result && (result.access === "true" || result.access === true)) {
        setLoading(true);

        const initialUser = mapUserData(result, null);
        setUser(initialUser);

        if (shouldNavigate) {
          const targetPath = redirectPath || "/dashboard";
          const decodedPath = decodeURIComponent(targetPath);
          navigate(decodedPath, { replace: true });
        }

        try {
          const profileData = await getCustomerByIdd();
          const fullUser = mapUserData(result, profileData);
          setUser(fullUser);
          return fullUser;
        } catch (e) {
          console.error("Failed to fetch full profile after login:", e);
          return initialUser;
        } finally {
          setLoading(false);
        }
      }

      return null;
    },
    [navigate, mapUserData],
  );

  const logout = useCallback(async () => {
    try {
      await logoutCustomer();
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      setUser(null);
      navigate("/auth", { replace: true });
    }
  }, [navigate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        fetchUser,
        refreshUser: fetchUser,
        handleAuthSuccess,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
